import { CalendarClock, Check, Pencil, Sparkles, Trash2, Video } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { openHubAi } from "@/lib/hubAssist";
import { matchesHubSearch, useHubSearchQuery } from "@/lib/hubSearch";
import { peekModuleCache, writeModuleCache } from "@/lib/moduleCache";
import {
  createTask,
  deleteTask,
  fetchTasks,
  formatDue,
  localDay,
  TASKS_CHANGED_EVENT,
  updateTask,
  type HubTask,
  type HubTaskPriority,
} from "@/lib/tasks";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { cn } from "@/lib/utils";

const TASKS_CACHE_KEY = "tasks";
const TASKS_REFRESH_MS = 120_000;

type View = "open" | "done";

function taskError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return "Couldn't reach the Hub API. Check your connection and try again.";
    if (err.status === 408) return "Hub took too long to answer. Try again.";
    return err.message || fallback;
  }
  return fallback;
}

function groupOpen(tasks: HubTask[]) {
  const today = localDay();
  const byDue = (a: HubTask, b: HubTask) =>
    (a.due || "9999").localeCompare(b.due || "9999") || priorityRank(b) - priorityRank(a) || b.createdAt.localeCompare(a.createdAt);
  const open = tasks.filter((t) => t.status === "open").sort(byDue);
  return [
    { key: "overdue", label: "Overdue", tasks: open.filter((t) => t.due && t.due < today) },
    { key: "today", label: "Today", tasks: open.filter((t) => t.due === today) },
    { key: "upcoming", label: "Upcoming", tasks: open.filter((t) => t.due && t.due > today) },
    { key: "someday", label: "No date", tasks: open.filter((t) => !t.due) },
  ].filter((group) => group.tasks.length);
}

function priorityRank(task: HubTask) {
  return task.priority === "high" ? 2 : task.priority === "low" ? 0 : 1;
}

type EditState = { title: string; notes: string; due: string; priority: HubTaskPriority };

function TaskRow({
  task,
  onToggle,
  onSave,
  onDelete,
  onSource,
}: {
  task: HubTask;
  onToggle: () => void;
  onSave: (edit: EditState) => Promise<boolean>;
  onDelete: () => void;
  onSource: () => void;
}) {
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const overdue = task.status === "open" && task.due && task.due < localDay();
  const done = task.status === "done";

  if (edit) {
    return (
      <li className="task-row is-editing">
        <form
          className="task-edit"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!edit.title.trim()) return;
            setSaving(true);
            const ok = await onSave(edit);
            setSaving(false);
            if (ok) setEdit(null);
          }}
        >
          <input
            className="task-input"
            aria-label="Task title"
            value={edit.title}
            maxLength={300}
            autoFocus
            onChange={(e) => setEdit({ ...edit, title: e.target.value })}
          />
          <textarea
            className="task-input"
            aria-label="Notes"
            placeholder="Notes"
            rows={3}
            maxLength={4000}
            value={edit.notes}
            onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
          />
          <div className="task-edit-row">
            <input
              type="date"
              className="task-input"
              aria-label="Due date"
              value={edit.due}
              onChange={(e) => setEdit({ ...edit, due: e.target.value })}
            />
            <select
              className="task-input"
              aria-label="Priority"
              value={edit.priority}
              onChange={(e) => setEdit({ ...edit, priority: e.target.value as HubTaskPriority })}
            >
              <option value="high">High priority</option>
              <option value="normal">Normal priority</option>
              <option value="low">Low priority</option>
            </select>
            <span className="task-edit-spacer" />
            <button type="button" className="secondary-btn" disabled={saving} onClick={() => setEdit(null)}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={saving || !edit.title.trim()}>
              Save
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className={cn("task-row", done && "is-done")}>
      <button
        type="button"
        className={cn("task-check", done && "checked")}
        aria-label={done ? `Reopen ${task.title}` : `Mark ${task.title} done`}
        aria-pressed={done}
        onClick={onToggle}
      >
        {done ? <Check size={13} aria-hidden /> : null}
      </button>
      <div className="task-copy">
        <p className="task-title">{task.title}</p>
        {task.notes ? <p className="task-notes">{task.notes}</p> : null}
        <p className="task-meta">
          {task.due ? (
            <span className={cn("task-due", overdue && "overdue")}>
              <CalendarClock size={12} aria-hidden /> {formatDue(task.due)}
            </span>
          ) : null}
          {task.priority === "high" ? <span className="pill task-priority-high">High</span> : null}
          {task.priority === "low" ? <span className="pill">Low</span> : null}
          {task.source && task.source.kind !== "manual" && task.source.title ? (
            <button type="button" className="task-source" onClick={onSource} title="Open where this came from">
              {task.source.kind === "meeting" ? <Video size={12} aria-hidden /> : <Sparkles size={12} aria-hidden />}
              {task.source.title}
            </button>
          ) : task.source?.kind === "ai" ? (
            <span className="task-source is-static">
              <Sparkles size={12} aria-hidden /> Hub AI
            </span>
          ) : null}
        </p>
      </div>
      <div className="task-actions">
        <button
          type="button"
          className="icon-btn"
          aria-label={`Edit ${task.title}`}
          title="Edit"
          onClick={() =>
            setEdit({ title: task.title, notes: task.notes, due: task.due || "", priority: task.priority })
          }
        >
          <Pencil size={15} />
        </button>
        <button type="button" className="icon-btn" aria-label={`Delete ${task.title}`} title="Delete" onClick={onDelete}>
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}

export function TasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<HubTask[]>(() => peekModuleCache<HubTask[]>(TASKS_CACHE_KEY) ?? []);
  const [loaded, setLoaded] = useState(() => Boolean(peekModuleCache(TASKS_CACHE_KEY)));
  const [error, setError] = useState("");
  const [view, setView] = useState<View>("open");
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<HubTaskPriority>("normal");
  const [adding, setAdding] = useState(false);

  const commit = useCallback((next: HubTask[]) => {
    setTasks(next);
    void writeModuleCache(TASKS_CACHE_KEY, next, { mirror: true });
  }, []);

  const load = useCallback(async () => {
    try {
      commit(await fetchTasks("all"));
      setError("");
    } catch (err) {
      setError(taskError(err, "Tasks unavailable."));
    } finally {
      setLoaded(true);
    }
  }, [commit]);

  useEffect(() => {
    void load();
  }, [load]);
  useLiveRefresh(() => void load(), TASKS_REFRESH_MS);
  useEffect(() => {
    const onChanged = () => void load();
    window.addEventListener(TASKS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
  }, [load]);

  const searchQuery = useHubSearchQuery("tasks").trim();
  const shownTasks = useMemo(
    () => (searchQuery ? tasks.filter((t) => matchesHubSearch(searchQuery, t.title, t.notes)) : tasks),
    [tasks, searchQuery],
  );
  const groups = useMemo(() => groupOpen(shownTasks), [shownTasks]);
  const doneTasks = useMemo(
    () =>
      shownTasks
        .filter((t) => t.status === "done")
        .sort((a, b) => (b.completedAt || "").localeCompare(a.completedAt || "")),
    [shownTasks],
  );
  const doneCount = useMemo(() => tasks.filter((t) => t.status === "done").length, [tasks]);
  const openCount = tasks.length - doneCount;

  async function onAdd(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || adding) return;
    setAdding(true);
    try {
      const task = await createTask({ title: title.trim(), due: due || null, priority });
      commit([task, ...tasks]);
      setTitle("");
      setDue("");
      setPriority("normal");
      setError("");
    } catch (err) {
      setError(taskError(err, "Couldn't add that task."));
    } finally {
      setAdding(false);
    }
  }

  async function patch(task: HubTask, change: Parameters<typeof updateTask>[1]): Promise<boolean> {
    const before = tasks;
    commit(tasks.map((t) => (t.id === task.id ? { ...t, ...change } as HubTask : t)));
    try {
      const saved = await updateTask(task.id, change);
      setTasks((list) => {
        const next = list.map((t) => (t.id === saved.id ? saved : t));
        void writeModuleCache(TASKS_CACHE_KEY, next, { mirror: true });
        return next;
      });
      return true;
    } catch (err) {
      commit(before);
      setError(taskError(err, "Couldn't save that change."));
      return false;
    }
  }

  async function remove(task: HubTask) {
    const before = tasks;
    commit(tasks.filter((t) => t.id !== task.id));
    try {
      await deleteTask(task.id);
    } catch (err) {
      commit(before);
      setError(taskError(err, "Couldn't delete that task."));
    }
  }

  function openSource(task: HubTask) {
    const kind = task.source?.kind;
    if (kind === "meeting") navigate("/meetings");
    else if (kind === "calendar") navigate("/calendar");
    else if (kind === "mail") navigate("/mail");
    else if (kind === "chat") navigate("/chat");
  }

  const row = (task: HubTask) => (
    <TaskRow
      key={task.id}
      task={task}
      onToggle={() => void patch(task, { status: task.status === "done" ? "open" : "done" })}
      onSave={(edit) =>
        patch(task, { title: edit.title.trim(), notes: edit.notes, due: edit.due || null, priority: edit.priority })
      }
      onDelete={() => void remove(task)}
      onSource={() => openSource(task)}
    />
  );

  return (
    <div className="page-pad tasks-page">
      <div className="tasks-head">
        <div>
          <h1>Tasks</h1>
          <p className="muted">
            {openCount} open{doneCount ? ` · ${doneCount} done` : ""}
          </p>
        </div>
        <button
          type="button"
          className="secondary-btn tasks-ai-btn"
          onClick={() => openHubAi({ prompt: "Plan my day around my meetings and open tasks", send: true })}
        >
          <Sparkles size={15} aria-hidden /> Plan my day
        </button>
      </div>

      <form className="task-add" onSubmit={onAdd}>
        <input
          className="task-input task-add-title"
          aria-label="New task"
          placeholder="Add a task"
          value={title}
          maxLength={300}
          onChange={(e) => setTitle(e.target.value)}
        />
        <input
          type="date"
          className="task-input"
          aria-label="Due date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
        />
        <select
          className="task-input"
          aria-label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as HubTaskPriority)}
        >
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
        <button type="submit" className="primary-btn" disabled={adding || !title.trim()}>
          Add
        </button>
      </form>

      <div className="tasks-tabs" role="tablist" aria-label="Task view">
        <button type="button" role="tab" aria-selected={view === "open"} className={cn(view === "open" && "active")} onClick={() => setView("open")}>
          Open
        </button>
        <button type="button" role="tab" aria-selected={view === "done"} className={cn(view === "done" && "active")} onClick={() => setView("done")}>
          Done
        </button>
      </div>

      {error ? <p className="banner-error">{error}</p> : null}

      {view === "open" ? (
        <>
          {groups.map((group) => (
            <section key={group.key} className="tasks-group">
              <h2 className={cn("tasks-group-title", group.key === "overdue" && "overdue")}>
                {group.label} <span className="muted">{group.tasks.length}</span>
              </h2>
              <ul className="tasks-list">{group.tasks.map(row)}</ul>
            </section>
          ))}
          {loaded && !groups.length && searchQuery ? (
            <p className="muted">No open tasks match “{searchQuery}”.</p>
          ) : null}
          {loaded && !groups.length && !searchQuery ? (
            <div className="tasks-empty">
              <p>No open tasks.</p>
              <p className="muted">
                Add one above, or ask Hub AI to turn a meeting into follow-ups.
              </p>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => openHubAi({ prompt: "What follow-ups do I owe from today's meetings?", send: true })}
              >
                <Sparkles size={15} aria-hidden /> Find follow-ups
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <section className="tasks-group">
          <ul className="tasks-list">{doneTasks.slice(0, 100).map(row)}</ul>
          {loaded && !doneTasks.length ? (
            <p className="muted">
              {searchQuery ? `No done tasks match “${searchQuery}”.` : "Nothing done yet."}
            </p>
          ) : null}
        </section>
      )}
    </div>
  );
}
